import type {
  MatrixChainFormState,
  MatrixLevelFormState,
  MatrixValidationError,
} from '../types/escalationMatrix.types';
import type { EscalationContact } from '../types/escalationContact.types';

export function isLevelUnsetOptional(level: MatrixLevelFormState): boolean {
  return Boolean(level.isOptional && !level.role);
}

export const LEVEL_TIMER_ERROR_MESSAGE = 'Timer must be greater than 0 minutes';
export const CHAIN_ZERO_TIMER_MESSAGE = 'Every level timer must be greater than zero.';

export function getLevelTimerError(level: MatrixLevelFormState): string | undefined {
  if (isLevelUnsetOptional(level) || !level.role) {
    return undefined;
  }
  if (!level.timerMinutes || level.timerMinutes <= 0) {
    return LEVEL_TIMER_ERROR_MESSAGE;
  }
  return undefined;
}

export function chainHasZeroTimerError(levels: MatrixLevelFormState[]): boolean {
  return levels.some(level => Boolean(getLevelTimerError(level)));
}

function isLevelConfigured(level: MatrixLevelFormState): boolean {
  if (isLevelUnsetOptional(level)) {
    return false;
  }
  return Boolean(level.role && level.contactId && level.timerMinutes > 0);
}

export function getConfiguredLevels(levels: MatrixLevelFormState[]): MatrixLevelFormState[] {
  return levels.filter(isLevelConfigured);
}

export function validateMatrixChain(
  chain: MatrixChainFormState,
  activeContacts: EscalationContact[]
): MatrixValidationError[] {
  const errors: MatrixValidationError[] = [];
  const contactsById = new Map(activeContacts.map(c => [c.id, c]));
  const configured = getConfiguredLevels(chain.levels);

  if (!configured.length) {
    errors.push({ levelNumber: 1, field: 'contactId', message: 'Level 1 is required' });
  }

  const levelOne = chain.levels.find(level => level.levelNumber === 1);
  if (!levelOne || !isLevelConfigured(levelOne)) {
    if (!levelOne?.role) {
      errors.push({ levelNumber: 1, field: 'role', message: 'Role is required' });
    }
    if (!levelOne?.contactId) {
      errors.push({ levelNumber: 1, field: 'contactId', message: 'Contact is required' });
    }
    if (!levelOne?.timerMinutes || levelOne.timerMinutes <= 0) {
      errors.push({ levelNumber: 1, field: 'timerMinutes', message: LEVEL_TIMER_ERROR_MESSAGE });
    }
  }

  if (!chain.reminderIntervalMinutes || chain.reminderIntervalMinutes <= 0) {
    errors.push({
      field: 'reminderIntervalMinutes',
      message: 'Reminder frequency must be greater than 0',
    });
  }

  if (!chain.maxReminderAttempts || chain.maxReminderAttempts <= 0) {
    errors.push({
      field: 'maxReminderAttempts',
      message: 'Maximum reminder attempts must be greater than 0',
    });
  }

  configured.forEach(level => {
    if (!level.role) {
      errors.push({ levelNumber: level.levelNumber, field: 'role', message: 'Role is required' });
    }
    if (!level.contactId) {
      errors.push({
        levelNumber: level.levelNumber,
        field: 'contactId',
        message: 'Contact is required',
      });
    } else {
      const contact = contactsById.get(level.contactId);
      if (!contact || contact.status !== 'Active') {
        errors.push({
          levelNumber: level.levelNumber,
          field: 'contactId',
          message: 'Contact must be active',
        });
      } else if (level.role && contact.role !== level.role) {
        errors.push({
          levelNumber: level.levelNumber,
          field: 'contactId',
          message: `Contact must match role ${level.role}`,
        });
      }
    }
    const timerError = getLevelTimerError(level);
    if (timerError) {
      errors.push({
        levelNumber: level.levelNumber,
        field: 'timerMinutes',
        message: timerError,
      });
    }
  });

  chain.levels.forEach(level => {
    if (isLevelUnsetOptional(level) || !level.role) {
      return;
    }
    const timerError = getLevelTimerError(level);
    if (
      timerError &&
      !errors.some(err => err.levelNumber === level.levelNumber && err.field === 'timerMinutes')
    ) {
      errors.push({
        levelNumber: level.levelNumber,
        field: 'timerMinutes',
        message: timerError,
      });
    }
  });

  const seenContactIds = new Set<number>();
  chain.levels.forEach(level => {
    if (!level.role || isLevelUnsetOptional(level)) {
      return;
    }

    if (level.contactId != null) {
      if (seenContactIds.has(level.contactId)) {
        errors.push({
          levelNumber: level.levelNumber,
          field: 'contactId',
          message: 'This contact is already assigned to another level in the chain',
        });
      }
      seenContactIds.add(level.contactId);
    }
  });

  return errors;
}

export function validateFacilityActivation(
  chains: Record<'Major' | 'Minor', MatrixChainFormState>
): MatrixValidationError[] {
  const errors: MatrixValidationError[] = [];

  (['Major', 'Minor'] as const).forEach(band => {
    const configured = getConfiguredLevels(chains[band].levels);
    if (!configured.length) {
      errors.push({
        criticalityBand: band,
        field: 'criticalityBand',
        message: `${band} criticality band must have at least one escalation level`,
      });
    }
  });

  return errors;
}

export function validateForActivation(
  chains: Record<'Major' | 'Minor', MatrixChainFormState>,
  activeContacts: EscalationContact[]
): MatrixValidationError[] {
  const errors: MatrixValidationError[] = [];

  (['Major', 'Minor'] as const).forEach(band => {
    validateMatrixChain(chains[band], activeContacts).forEach(error => {
      errors.push({ ...error, criticalityBand: band });
    });
  });

  validateFacilityActivation(chains).forEach(error => {
    if (!errors.some(existing => existing.message === error.message)) {
      errors.push(error);
    }
  });

  return errors;
}

export function groupActivationErrors(errors: MatrixValidationError[]) {
  const general: MatrixValidationError[] = [];
  const byBand: Record<string, MatrixValidationError[]> = { Major: [], Minor: [] };

  errors.forEach(error => {
    if (error.criticalityBand) {
      byBand[error.criticalityBand].push(error);
    } else {
      general.push(error);
    }
  });

  return { general, byBand };
}

export function groupFieldErrorsByLevel(errors: MatrixValidationError[]) {
  const grouped: Record<
    number,
    { role?: string; contactId?: string; timerMinutes?: string; general?: string }
  > = {};
  errors.forEach(error => {
    if (error.levelNumber == null) {
      return;
    }
    const current = grouped[error.levelNumber] || {};
    if (error.field === 'role') {
      current.role = error.message;
    } else if (error.field === 'contactId') {
      current.contactId = error.message;
    } else if (error.field === 'timerMinutes') {
      current.timerMinutes = error.message;
    } else {
      current.general = error.message;
    }
    grouped[error.levelNumber] = current;
  });
  return grouped;
}

export function parseTimerInput(value: string): number {
  const digits = String(value || '').replace(/\D/g, '');
  if (!digits) {
    return 0;
  }
  return Math.min(999, Number.parseInt(digits, 10));
}
